
import type { PropType } from 'vue'
import type { ICarouselSlide } from '../models/components/i-carousel'
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer'
import { defineComponent } from 'vue'

import { TYPES } from '../config/inversify.types'

let JS_INTERVAL_REF
const START_SLIDE_INDEX = 0

export default defineComponent({
  name: 'Carousel',
  inject: {
    objectIdService: TYPES.ObjectIdService,
  },
  props: {
    slides: {
      type: Array as PropType<Array<ICarouselSlide>>,
      required: true,
    },
    interval: {
      type: Number,
      default: 7000,
    },
    autoplay: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      baseClass: 'epb-carousel',
      slideWidth: 0,
    }
  },
  computed: {
    activeSlideIndex(): number {
      return this.slides.findIndex((slide: ICarouselSlide): boolean => slide.active)
    },
    nextSlideIndex(): number {
      return this.activeSlideIndex < this.slides.length - 1
        ? this.activeSlideIndex + 1
        : START_SLIDE_INDEX
    },
    previousSlideIndex(): number {
      return this.activeSlideIndex === START_SLIDE_INDEX
        ? this.slides.length - 1
        : this.activeSlideIndex - 1
    },
  },
  mounted(): void {
    this.slides[START_SLIDE_INDEX].active = true
    if (this.autoplay) {
      JS_INTERVAL_REF = setInterval(() => {
        this.slideToIndex(this.nextSlideIndex)
      }, this.interval)
    }
    const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill
    this.resizeObserver = new ResizeObserver(rootEntry => {
      const rootElement = rootEntry[0].target as HTMLElement
      this.slideWidth = rootElement.getBoundingClientRect().width
    })
    this.resizeObserver.observe(this.$el)
  },
  beforeDestroy(): void {
    this.slides.forEach((slide: ICarouselSlide) => (slide.active = false))
    clearInterval(JS_INTERVAL_REF)
  },
  methods: {
    slideToIndex(slideIndex: number): void {
      this.slides.forEach((slide: ICarouselSlide, index: number): void => {
        slide.active = index === slideIndex
      })
      const container = this.$refs.container
      container.scrollTo({ left: slideIndex * this.slideWidth })
    },
    handleSlideClick(slideIndex: number): void {
      clearInterval(JS_INTERVAL_REF)
      this.slideToIndex(slideIndex)
      this.$emit('slideUpdated', slideIndex)
    },
  },
})
